import React from "react";
import { Link, useNavigate } from "react-router-dom";
import IntegrationActivityLog from "../IntegrationActivityLog";
import { SectionCard } from "@/components/ds";
import IconByName from "@/components/ds/IconByName";
import IntegrationStickyHeader, { IntegrationTab } from "../shared/IntegrationStickyHeader";

const TEAMS_COLOR = "#0078D4";

export default function TeamsPhone() {
  const [tab, setTab] = React.useState<"overview" | "activity">("overview");

  const tabsJSX = (
    <>
      <IntegrationTab active={tab === "overview"} onClick={() => setTab("overview")}>Overview</IntegrationTab>
      <IntegrationTab active={tab === "activity"} onClick={() => setTab("activity")}>Activity Log</IntegrationTab>
    </>
  );

  return (
    <IntegrationStickyHeader title="Microsoft Teams Phone" tabs={tabsJSX}>
      {tab === "overview" && (
        <div className="space-y-4">
          <SectionCard title="Teams Phone CTI Integration">
            <div className="text-zinc-500 text-sm leading-relaxed mb-4">
              Connect Microsoft Teams Phone System (Direct Routing or Calling Plans) to get a full customer screen pop on every inbound call. When a call arrives, Velaro automatically searches your connected CRM, service, and e-commerce integrations and surfaces matching customer data in the agent sidebar — before they answer.
            </div>
            <div className="flex flex-wrap gap-2 mb-4">
              <span className="text-xs font-semibold px-2 py-1 rounded-full bg-blue-100 text-blue-800">Screen pop</span>
              <span className="text-xs font-semibold px-2 py-1 rounded-full bg-zinc-100 text-zinc-600">Call logging</span>
              <span className="text-xs font-semibold px-2 py-1 rounded-full bg-zinc-100 text-zinc-600">IVR hand-off</span>
              <span className="text-xs font-semibold px-2 py-1 rounded-full bg-zinc-100 text-zinc-600">Microsoft 365</span>
            </div>
            <div className="p-4 rounded-xl bg-blue-50 border border-blue-200 text-sm text-blue-900">
              <strong>Setup required.</strong> Teams Phone integration uses the Microsoft Graph Communications API
              and requires an Azure app registration with <code className="bg-blue-100 px-1 rounded">Calls.AccessMedia.All</code> and{" "}
              <code className="bg-blue-100 px-1 rounded">Calls.Initiate.All</code> permissions granted by your Microsoft 365 admin.
              Contact <strong>support@velaro.com</strong> or your account manager to begin setup.
            </div>
          </SectionCard>

          <SectionCard title="What you get" description="All features use the same screen pop provider system as Nextiva — your CRM integrations are automatically available.">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { icon: "Phone", title: "Inbound call screen pop", desc: "When a Teams Phone call arrives, Velaro searches all your integrations (HubSpot, Salesforce, ServiceTitan, etc.) and shows the caller's profile in the agent sidebar." },
                { icon: "Link", title: "Direct Routing + Calling Plans", desc: "Works with both SBC-based Direct Routing and Microsoft-hosted Calling Plans. No third-party PBX required." },
                { icon: "FileText", title: "Post-call activity logging", desc: "When a call ends, Velaro creates a call note on the matched contact in your CRM — Dynamics 365, HubSpot, Salesforce, or Velaro CRM." },
                { icon: "Bot", title: "IVR to Teams hand-off", desc: "Calls handled by Velaro IVR can transfer into Teams Phone queues. Full conversation context travels with the call." },
              ].map((item) => (
                <div key={item.title} className="flex gap-3 p-3 rounded-xl border border-zinc-100 bg-zinc-50">
                  <span className="flex-shrink-0 text-zinc-600"><IconByName name={item.icon} className="w-5 h-5" /></span>
                  <div>
                    <div className="text-sm font-semibold text-zinc-700 mb-1">{item.title}</div>
                    <div className="text-xs text-zinc-500 leading-relaxed">{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>

          <SectionCard title="How screen pop works" description="Teams Phone uses the same automatic lookup system as all Velaro phone integrations.">
            <div className="text-sm text-zinc-600 mb-4 leading-relaxed">
              Every enabled CRM and service integration on your site is automatically checked when a call arrives. The lookup runs in parallel across all of them — HubSpot, Salesforce, Dynamics 365, ServiceTitan, ServiceNow, VAST, and more. The first match wins and appears in the agent sidebar.
            </div>
            <div className="p-3 bg-zinc-50 rounded-xl border border-zinc-200 text-xs text-zinc-500">
              See the{" "}
              <Link to="/integrations/nextiva" className="text-[var(--color-primary)] underline">
                Nextiva integration page
              </Link>{" "}
              for the full list of integrations with screen pop support and a detailed explanation of how the lookup works.
            </div>
          </SectionCard>

          <SectionCard title="Setup process">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { step: "1", title: "Contact Velaro", desc: "Email support@velaro.com or reach out to your account manager. A Velaro engineer will guide you through the Azure setup." },
                { step: "2", title: "Register Azure app", desc: "Create an app registration in your Azure AD tenant with Graph Communications API permissions. Velaro support will provide the exact scopes needed." },
                { step: "3", title: "Configure webhook", desc: "Velaro generates a call event webhook endpoint. Add it to your Teams Phone configuration to start receiving call events." },
              ].map((s) => (
                <div key={s.step} className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full text-white text-xs flex items-center justify-center font-bold mt-0.5" style={{ backgroundColor: TEAMS_COLOR }}>{s.step}</span>
                  <div>
                    <div className="text-sm font-semibold text-zinc-700 mb-1">{s.title}</div>
                    <div className="text-xs text-zinc-500 leading-relaxed">{s.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>
        </div>
      )}

      {tab === "activity" && <IntegrationActivityLog integrationSlug="TeamsPhone" />}
    </IntegrationStickyHeader>
  );
}

export function BackToIntegrationLandingTeamsPhone() {
  const navigate = useNavigate();
  return (
    <button type="button" onClick={() => navigate("/integrations")} className="text-sm text-zinc-500 hover:text-zinc-700 flex items-center gap-1">
      ← Integrations
    </button>
  );
}
